import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * DataQualityPanel
 * --------------------------------------------------------------------------
 * Surfaces data validation problems on the customer's HRIS sync. Three
 * sections: KPI overview, validation issues list with filters, and
 * field-completeness bars.
 *
 * Each issue can be opened in a Details modal that shows:
 *   - the exact validation rule
 *   - why it matters for pay-equity analysis (Article 9 framing)
 *   - sample affected records (privacy-redacted)
 *   - a clear path to fix (which makes Resolve actually meaningful)
 *
 * Severity badge colours intentionally use solid `bg-warning text-warning-foreground`
 * (white-on-amber, readable) for the badge, while inline text uses `text-warning`
 * (dark amber on near-white), this is the same trap that bit FIXED32.
 */

const DataQualityPanel = ({ qualityMetrics, validationErrors, onResolveError, onRefreshValidation }) => {
  const { t, i18n } = useTranslation();
  const [selectedErrorType, setSelectedErrorType] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [detailsErrorId, setDetailsErrorId] = useState(null);

  const errorTypes = [
    { value: 'all',       labelKey: 'dataIntegration.quality.filterAll' },
    { value: 'missing',   labelKey: 'dataIntegration.quality.filterMissing' },
    { value: 'invalid',   labelKey: 'dataIntegration.quality.filterInvalid' },
    { value: 'duplicate', labelKey: 'dataIntegration.quality.filterDuplicate' },
    { value: 'mapping',   labelKey: 'dataIntegration.quality.filterMapping' },
  ].map((tab) => ({
    ...tab,
    count: tab.value === 'all'
      ? validationErrors?.length || 0
      : validationErrors?.filter((e) => e?.type === tab.value)?.length || 0,
  }));

  const filteredErrors = selectedErrorType === 'all'
    ? validationErrors
    : validationErrors?.filter((error) => error?.type === selectedErrorType);

  const detailsError = validationErrors?.find((e) => e?.id === detailsErrorId) || null;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefreshValidation();
    setIsRefreshing(false);
  };

  const getErrorIcon = (type) => {
    switch (type) {
      case 'missing':   return 'AlertCircle';
      case 'invalid':   return 'XCircle';
      case 'duplicate': return 'Copy';
      case 'mapping':   return 'ArrowRightLeft';
      default:          return 'AlertTriangle';
    }
  };

  const getErrorColor = (severity) => {
    switch (severity) {
      case 'high':   return 'text-destructive';
      case 'medium': return 'text-warning';
      case 'low':    return 'text-muted-foreground';
      default:       return 'text-muted-foreground';
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      high:   'bg-destructive/10 text-destructive',
      medium: 'bg-warning/10 text-warning',
      low:    'bg-muted text-muted-foreground',
    };
    const labelKey = `dataIntegration.quality.severity.${severity}`;
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors?.[severity]}`}>
        {t(labelKey, { defaultValue: (severity || '').toUpperCase() })}
      </span>
    );
  };

  // Format "X hours ago" / "vor X Stunden" using i18n count interpolation
  const formatHoursAgo = (hours) => {
    if (!hours || hours < 1) return t('dataIntegration.quality.justNow');
    return t('dataIntegration.quality.hoursAgo', { count: hours });
  };

  return (
    <div className="space-y-6">
      {/* Data Quality Overview */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">{t('dataIntegration.quality.sectionTitle')}</h3>
          <Button
            variant="outline"
            loading={isRefreshing}
            iconName="RefreshCw"
            iconPosition="left"
            onClick={handleRefresh}
          >
            {t('dataIntegration.quality.refresh')}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Database" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{t('dataIntegration.quality.totalRecords')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{qualityMetrics?.totalRecords?.toLocaleString(i18n.language)}</p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-foreground">{t('dataIntegration.quality.validRecords')}</span>
            </div>
            <p className="text-2xl font-bold text-success">{qualityMetrics?.validRecords?.toLocaleString(i18n.language)}</p>
            <p className="text-sm text-muted-foreground">
              {t('dataIntegration.quality.percentOfTotal', { value: qualityMetrics?.validPercentage })}
            </p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span className="text-sm font-medium text-foreground">{t('dataIntegration.quality.issuesFound')}</span>
            </div>
            <p className="text-2xl font-bold text-warning">{qualityMetrics?.issuesFound}</p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{t('dataIntegration.quality.completeness')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-background rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full"
                  style={{ width: `${qualityMetrics?.completeness}%` }}
                />
              </div>
              <span className="text-sm font-medium text-foreground">{qualityMetrics?.completeness}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('dataIntegration.quality.validationIssues')}</h3>

        <div className="flex flex-wrap gap-2 mb-6">
          {errorTypes?.map((type) => (
            <button
              key={type.value}
              onClick={() => setSelectedErrorType(type.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedErrorType === type.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(type.labelKey)} ({type.count})
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filteredErrors?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">{t('dataIntegration.quality.noIssues')}</h4>
              <p className="text-muted-foreground">{t('dataIntegration.quality.noIssuesBody')}</p>
            </div>
          ) : (
            filteredErrors?.map((error) => (
              <div key={error?.id} className="flex items-start justify-between p-4 bg-muted rounded-lg gap-4">
                <div className="flex items-start space-x-3 min-w-0">
                  <Icon
                    name={getErrorIcon(error?.type)}
                    size={20}
                    className={`${getErrorColor(error?.severity)} shrink-0 mt-0.5`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <h4 className="font-medium text-foreground">{t(error.titleKey)}</h4>
                      {getSeverityBadge(error?.severity)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{t(error.descriptionKey)}</p>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{t('dataIntegration.quality.fieldLabel')}: <span className="font-mono text-foreground">{error?.field}</span></span>
                      <span>{t('dataIntegration.quality.recordsAffected')}: <span className="font-medium text-foreground">{error?.recordCount}</span></span>
                      <span>{t('dataIntegration.quality.detected')}: <span className="text-foreground">{formatHoursAgo(error?.detectedHoursAgo)}</span></span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => setDetailsErrorId(error?.id)}
                  >
                    {t('dataIntegration.quality.viewDetails')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="CheckCircle"
                    iconPosition="left"
                    onClick={() => onResolveError(error?.id)}
                  >
                    {t('dataIntegration.quality.resolve')}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Field Completeness */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('dataIntegration.quality.fieldCompleteness')}</h3>
        <div className="space-y-4">
          {qualityMetrics?.fieldCompleteness?.map((field) => (
            <div key={field?.name} className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">
                    {field?.labelKey ? t(field.labelKey) : field?.label}
                  </span>
                  <span className="text-sm text-muted-foreground">{field?.completeness}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      field?.completeness >= 90 ? 'bg-success' :
                      field?.completeness >= 70 ? 'bg-warning' : 'bg-destructive'
                    }`}
                    style={{ width: `${field?.completeness}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Details Modal */}
      {detailsError && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setDetailsErrorId(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-md ${
                  detailsError.severity === 'high' ? 'bg-destructive/10' : 'bg-warning/10'
                } flex items-center justify-center shrink-0`}>
                  <Icon
                    name={getErrorIcon(detailsError.type)}
                    size={20}
                    className={getErrorColor(detailsError.severity)}
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-foreground">{t(detailsError.titleKey)}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span>{t('dataIntegration.quality.fieldLabel')}: <span className="font-mono text-foreground">{detailsError.field}</span></span>
                    <span>·</span>
                    <span>{t('dataIntegration.quality.recordsAffected')}: <span className="text-foreground">{detailsError.recordCount}</span></span>
                    {getSeverityBadge(detailsError.severity)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setDetailsErrorId(null)}
                className="text-muted-foreground hover:text-foreground p-1"
                aria-label={t('common.close')}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-5">
              {/* What's wrong */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  {t('dataIntegration.quality.detailsWhatIsWrong')}
                </p>
                <p className="text-sm text-foreground">{t(detailsError.descriptionKey)}</p>
              </div>

              {/* Validation rule */}
              {detailsError.ruleKey && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                    {t('dataIntegration.quality.detailsRule')}
                  </p>
                  <p className="text-sm text-foreground bg-muted/50 border border-border rounded p-3 font-mono">
                    {t(detailsError.ruleKey)}
                  </p>
                </div>
              )}

              {/* Why it matters */}
              {detailsError.impactKey && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                    {t('dataIntegration.quality.detailsImpact')}
                  </p>
                  <div className="flex items-start gap-2 bg-warning/5 border border-warning/20 rounded p-3">
                    <Icon name="AlertTriangle" size={14} className="text-warning shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground">{t(detailsError.impactKey)}</p>
                  </div>
                </div>
              )}

              {/* Sample affected records */}
              {detailsError.sampleRecords?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                    {t('dataIntegration.quality.detailsSampleRecords', { count: detailsError.recordCount })}
                  </p>
                  <div className="border border-border rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                            {t('dataIntegration.quality.detailsTableId')}
                          </th>
                          <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                            {t('dataIntegration.quality.detailsTableDept')}
                          </th>
                          <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                            {t('dataIntegration.quality.detailsTableIssue')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {detailsError.sampleRecords.map((r, i) => (
                          <tr key={i} className="hover:bg-muted/20">
                            <td className="px-3 py-2 font-mono text-foreground">{r.id}</td>
                            <td className="px-3 py-2 text-muted-foreground">{r.department}</td>
                            <td className="px-3 py-2 text-muted-foreground">{r.issue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground italic mt-2">
                    {t('dataIntegration.quality.detailsPrivacyNote')}
                  </p>
                </div>
              )}

              {/* How to fix */}
              {detailsError.fixHintKey && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                    {t('dataIntegration.quality.detailsFixHint')}
                  </p>
                  <div className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded p-3">
                    <Icon name="Wrench" size={14} className="text-primary shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground">{t(detailsError.fixHintKey)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="p-5 border-t border-border flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setDetailsErrorId(null)}>
                {t('common.close')}
              </Button>
              <Button
                variant="default"
                iconName="CheckCircle"
                iconPosition="left"
                onClick={() => {
                  onResolveError(detailsError.id);
                  setDetailsErrorId(null);
                }}
              >
                {t('dataIntegration.quality.markResolved')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQualityPanel;
